const HEX_COLOR = /^#[0-9a-f]{6}$/i;

const FONT_STACKS = {
    inherit: 'inherit',
    system: 'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    serif: 'Georgia, "Times New Roman", Times, serif',
    poppins: '"calcimo-widget-poppins", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
};

const hexToRgb = (hex) => [1, 3, 5].map((offset) => parseInt(hex.slice(offset, offset + 2), 16));

const rgbToHex = (rgb) => `#${rgb.map((value) => Math.round(value).toString(16).padStart(2, '0')).join('')}`;

/**
 * Mix two hex colours; weight 0 returns the first colour, weight 1 the second.
 */
export const mixColors = (first, second, weight) => {
    const a = hexToRgb(first);
    const b = hexToRgb(second);

    return rgbToHex(a.map((value, index) => value + (b[index] - value) * weight));
};

const relativeLuminance = (hex) => {
    const [r, g, b] = hexToRgb(hex).map((value) => {
        const channel = value / 255;

        return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
    });

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/**
 * WCAG contrast ratio of two hex colours (1 to 21).
 */
export const contrastRatio = (first, second) => {
    const [lighter, darker] = [relativeLuminance(first), relativeLuminance(second)].sort((a, b) => b - a);

    return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Select the brand variant with the stronger contrast against the widget background.
 */
export const brandVariant = (background) => contrastRatio(background, '#edebd7')
    > contrastRatio(background, '#1f2330') ? 'light' : 'dark';

const color = (value, fallback) => (HEX_COLOR.test(value || '') ? value.toLowerCase() : fallback);

/**
 * Read the theme options of a widget link and fall back to the defaults for invalid values.
 */
export const readTheme = (anchor, defaults, fonts) => {
    const radius = Number.parseInt(anchor.dataset.radius ?? '', 10);
    const font = fonts.includes(anchor.dataset.font) ? anchor.dataset.font : defaults.font;
    const borderValue = anchor.dataset.border;

    return {
        accent: color(anchor.dataset.accent, defaults.accent),
        background: color(anchor.dataset.background, defaults.background),
        text: color(anchor.dataset.text, defaults.text),
        border: borderValue === 'none' ? 'none' : color(borderValue, defaults.border),
        radius: Number.isNaN(radius) ? defaults.radius : Math.min(Math.max(radius, 0), 24),
        font,
    };
};

/**
 * CSS custom properties of a theme, including derived colours with readable contrast.
 */
export const themeVariables = (theme) => {
    const { accent, background, text } = theme;
    const onAccent = contrastRatio(accent, '#ffffff') >= contrastRatio(accent, '#111111') ? '#ffffff' : '#111111';
    const highlight = contrastRatio(accent, background) >= 3 ? accent : text;
    const brandText = brandVariant(background) === 'light' ? '#edebd7' : '#1f2330';

    return {
        '--cw-accent': accent,
        '--cw-on-accent': onAccent,
        '--cw-highlight': highlight,
        '--cw-background': background,
        '--cw-text': text,
        '--cw-brand-text': brandText,
        '--cw-muted': mixColors(text, background, 0.35),
        '--cw-surface': mixColors(background, text, 0.04),
        '--cw-field-border': mixColors(background, text, 0.22),
        '--cw-focus': mixColors(accent, background, 0.4),
        '--cw-error': contrastRatio('#c0262d', background) >= 4.5 ? '#c0262d' : '#ff6b6b',
        '--cw-border': theme.border === 'none' ? 'transparent' : theme.border,
        '--cw-radius': `${theme.radius}px`,
        '--cw-field-radius': `${Math.min(theme.radius, 10)}px`,
        '--cw-font': FONT_STACKS[theme.font] ?? 'inherit',
    };
};

/**
 * Register the self-hosted Poppins font once in the host document, because @font-face
 * rules inside a shadow root are ignored by browsers.
 */
export const ensureFont = (theme, fontUrls, styleNonce = '') => {
    if (theme.font !== 'poppins' || document.getElementById('calcimo-widget-fonts')) {
        return;
    }

    const style = document.createElement('style');
    if (styleNonce) {
        style.nonce = styleNonce;
    }
    style.id = 'calcimo-widget-fonts';
    style.textContent = ['400', '600']
        .map((weight) => `@font-face{font-family:"calcimo-widget-poppins";font-style:normal;font-weight:${weight};font-display:swap;src:url("${fontUrls[`poppins-${weight}`]}") format("woff2");}`)
        .join('');
    document.head.appendChild(style);
};
